import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, AlertTriangle, Loader2, FileJson, Ban, Info } from "lucide-react";
import { api, apiError, complianceErrors } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * e-Invoice (IRN) status for one document.
 *
 * Deliberately quiet when it has nothing to say. It renders nothing at all
 * unless the organisation has switched e-Invoicing on in Settings, or this
 * document already carries an IRN. Once it is on, a B2C sale, a bill of
 * supply or a draft still needs no IRN, and those collapse to a single line
 * rather than a red panel that would train people to ignore it.
 */
export default function EInvoicePanel({ docId, canEdit, onChange }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/documents/${docId}/einvoice`)
      .then(({ data }) => setState(data))
      .catch(() => setState(null));
  }, [docId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!state) return null;

  const ei = state.einvoice || {};
  const registered = ei.status === "REGISTERED";
  const cancelled = ei.status === "CANCELLED";

  // e-Invoicing switched off in Settings: say nothing at all. Below the
  // turnover threshold it is voluntary and most businesses never enrol, so a
  // standing "not switched on" notice on every invoice is a prompt nobody
  // asked for — Settings is where that decision belongs.
  //
  // An IRN already obtained is the exception. It is a filed compliance record
  // and keeps showing even if the switch is turned off afterwards, because
  // hiding it would lose the only place the number is visible.
  if (!state.enabled && !registered && !cancelled) return null;

  // Nothing to do and nothing wrong — say so in one line and get out of the way.
  if (!registered && !cancelled && !state.eligible && !state.configured) {
    return (
      <div
        className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-[11px] text-slate-500"
        data-testid="einvoice-inactive"
      >
        <Info size={13} className="mt-0.5 shrink-0" />
        <span>
          No IRN required — {state.blockers[0] || "e-Invoicing does not apply to this document."}
        </span>
      </div>
    );
  }

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${docId}/einvoice/generate`);
      toast.success(`IRN generated — ${String(data.irn).slice(0, 16)}…`);
      load();
      onChange?.();
    } catch (e) {
      const details = complianceErrors(e);
      toast.error(details.length ? details[0].message : apiError(e));
      load();
    }
    setBusy(false);
  };

  const cancel = async () => {
    const remark = window.prompt(
      "Reason for cancelling this IRN (the IRP records it, and only allows this within 24 hours):",
      "Data entry mistake",
    );
    if (!remark) return;
    setBusy(true);
    try {
      await api.post(`/documents/${docId}/einvoice/cancel`, { reason_code: "2", remark });
      toast.success("IRN cancelled at the IRP");
      load();
      onChange?.();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const viewPayload = async () => {
    try {
      const { data } = await api.get(`/documents/${docId}/einvoice/payload`);
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `einvoice-${docId.slice(0, 8)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (registered) {
    return (
      <div
        className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3"
        data-testid="einvoice-registered"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
              <BadgeCheck size={15} /> Registered with the IRP
            </div>
            <div className="mt-1 break-all font-mono text-[10px] text-emerald-800">
              IRN {ei.irn}
            </div>
            <div className="mt-0.5 text-[11px] text-emerald-700">
              Ack {ei.ack_no} · {ei.ack_date}
              {ei.ewb_no ? ` · e-Way Bill ${ei.ewb_no}` : ""}
            </div>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={cancel}
              data-testid="einvoice-cancel"
              className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              <Ban size={14} /> Cancel IRN
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div
        className="rounded-md border border-slate-300 bg-slate-100 px-4 py-2.5 text-[11px] text-slate-600"
        data-testid="einvoice-cancelled"
      >
        <strong>IRN cancelled</strong> at the IRP{ei.cancel_reason ? ` — ${ei.cancel_reason}` : ""}.
        A cancelled IRN cannot be reinstated; issue a fresh invoice.
      </div>
    );
  }

  const problems = [...(state.blockers || []), ...(state.arithmetic_problems || [])];

  return (
    <div className="rounded-md border border-slate-200 bg-white px-4 py-3" data-testid="einvoice-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">e-Invoice (IRN)</div>
          {state.eligible ? (
            <p className="mt-0.5 text-[11px] text-slate-500">
              Ready to register with the Invoice Registration Portal.
            </p>
          ) : (
            <ul className="mt-1.5 space-y-1">
              {problems.map((p, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[11px] text-rose-700">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          )}
          {(state.warnings || []).map((w, i) => (
            <div key={i} className="mt-1 text-[11px] text-amber-700">
              {w}
            </div>
          ))}
          {ei.status === "FAILED" && ei.error && (
            <div className="mt-1.5 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-800">
              Last attempt was rejected: {ei.error}
            </div>
          )}
          {state.eligible && !state.configured && (
            <div className="mt-1.5 text-[11px] text-amber-700">{state.config_error}</div>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={viewPayload}
            disabled={!state.eligible} data-testid="einvoice-payload" className="gap-1.5">
            <FileJson size={14} /> JSON
          </Button>
          {canEdit && (
            <Button
              size="sm"
              disabled={busy || !state.eligible || !state.configured}
              onClick={generate}
              data-testid="einvoice-generate"
              className="gap-1.5 bg-blue-600 hover:bg-blue-700"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />}
              Generate IRN
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
